/**
 * KioskSetupDialog — configure a clock-in/out kiosk for a location-based
 * team (Jun 2026). Opened from the "Kiosk" button on Manage Teams.
 *
 * Backend: GET/POST /api/teams/{id}/kiosk, POST .../kiosk/rotate,
 *          POST .../kiosk/pin
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { MonitorSmartphone, Copy, RotateCw, Loader2, ExternalLink, KeyRound, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function KioskSetupDialog({ team, open, onClose }) {
  const [data, setData] = useState(null); // {exists, kiosk, members}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    location_label: '', enabled: true, auto_clockout_enabled: false,
    max_shift_hours: '', fixed_clockout_time: '',
    photo_on_punch: false, daily_summary_enabled: false, shift_start_time: '',
    announcement: '', announcement_expires_at: '', latitude: null, longitude: null,
  });
  const [pinInputs, setPinInputs] = useState({});
  const [pinBusy, setPinBusy] = useState(null);
  const [clockInMethod, setClockInMethod] = useState('any');

  const load = useCallback(async () => {
    if (!team?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/teams/${team.id}/kiosk`, { headers: authHeaders() });
      setData(r.data);
      const k = r.data.kiosk;
      setForm({
        location_label: k?.location_label ?? team.location ?? '',
        enabled: k ? !!k.enabled : true,
        auto_clockout_enabled: !!k?.auto_clockout_enabled,
        max_shift_hours: k?.max_shift_hours ?? '',
        fixed_clockout_time: k?.fixed_clockout_time ?? '',
        photo_on_punch: !!k?.photo_on_punch,
        daily_summary_enabled: !!k?.daily_summary_enabled,
        shift_start_time: k?.shift_start_time ?? '',
        announcement: k?.announcement ?? '',
        announcement_expires_at: k?.announcement_expires_at ?? '',
        latitude: k?.latitude ?? null,
        longitude: k?.longitude ?? null,
      });
      if (k?.kiosk_only) setClockInMethod('kiosk_only');
      else if (k?.qr_required) setClockInMethod('qr_required');
      else setClockInMethod('any');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load kiosk settings');
    } finally {
      setLoading(false);
    }
  }, [team?.id, team?.location]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        location_label: form.location_label,
        enabled: form.enabled,
        auto_clockout_enabled: form.auto_clockout_enabled,
        max_shift_hours: form.max_shift_hours === '' ? null : parseFloat(form.max_shift_hours),
        fixed_clockout_time: form.fixed_clockout_time || null,
        photo_on_punch: form.photo_on_punch,
        daily_summary_enabled: form.daily_summary_enabled,
        shift_start_time: form.shift_start_time || null,
        announcement: form.announcement || '',
        announcement_expires_at: form.announcement_expires_at || null,
        latitude: form.latitude,
        longitude: form.longitude,
        kiosk_only: clockInMethod === 'kiosk_only',
        qr_required: clockInMethod === 'qr_required',
      };
      const r = await axios.post(`${API}/teams/${team.id}/kiosk`, body, { headers: authHeaders() });
      setData((d) => ({ ...(d || {}), exists: true, kiosk: r.data.kiosk, members: r.data.members }));
      toast.success(data?.exists ? 'Kiosk settings saved.' : 'Kiosk created! Open the kiosk link on the on-site device.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save kiosk');
    } finally {
      setSaving(false);
    }
  };

  const rotate = async () => {
    try {
      const r = await axios.post(`${API}/teams/${team.id}/kiosk/rotate`, {}, { headers: authHeaders() });
      setData((d) => ({ ...d, kiosk: { ...d.kiosk, kiosk_token: r.data.kiosk_token } }));
      toast.success('New kiosk link generated — the old link no longer works.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not rotate link');
    }
  };

  const setPin = async (userId) => {
    const pin = (pinInputs[userId] || '').trim();
    if (!/^\d{4}$/.test(pin)) {
      toast.error('PIN must be exactly 4 digits.');
      return;
    }
    setPinBusy(userId);
    try {
      await axios.post(`${API}/teams/${team.id}/kiosk/pin`, { user_id: userId, pin }, { headers: authHeaders() });
      setPinInputs((p) => ({ ...p, [userId]: '' }));
      setData((d) => ({
        ...d,
        members: (d.members || []).map((m) => (m.id === userId ? { ...m, has_pin: true } : m)),
      }));
      toast.success('PIN saved.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set PIN');
    } finally {
      setPinBusy(null);
    }
  };

  const kiosk = data?.kiosk;
  const kioskUrl = kiosk?.kiosk_token ? `${window.location.origin}/kiosk/${kiosk.kiosk_token}` : null;

  const copyUrl = () => {
    if (!kioskUrl) return;
    navigator.clipboard.writeText(kioskUrl).then(
      () => toast.success('Kiosk link copied.'),
      () => toast.error('Could not copy — select and copy manually.'),
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-[95vw] sm:max-w-xl max-h-[88vh] overflow-y-auto" data-testid="kiosk-setup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MonitorSmartphone className="w-5 h-5 text-[#1E88E5]" />
            <span>Kiosk Mode — {team?.name}</span>
          </DialogTitle>
          <DialogDescription>
            Turn a tablet or computer at the job site into a clock-in/out station.
            Workers tap their name and enter their 4-digit PIN — no smartphone needed.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-5">
            {/* Kiosk link */}
            {kiosk && (
              <div className="rounded-lg p-3 space-y-2" style={{ background: 'rgba(30,136,229,0.06)', border: '1px solid rgba(30,136,229,0.25)' }}>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#7BB8E8' }}>
                  Kiosk device link {kiosk.enabled ? '' : '(kiosk disabled)'}
                </p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={kioskUrl || ''} className="text-xs font-mono" data-testid="kiosk-url-input" />
                  <Button size="sm" variant="outline" onClick={copyUrl} title="Copy link" data-testid="kiosk-copy-url-btn">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(kioskUrl, '_blank')} title="Open kiosk" data-testid="kiosk-open-url-btn">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={rotate} title="Generate a new link (old one stops working)" data-testid="kiosk-rotate-btn">
                    <RotateCw className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <p className="text-[11px]" style={{ color: '#64748B' }}>
                  Open this link on the on-site device. It stays active until you disable the kiosk or generate a new link.
                </p>
              </div>
            )}

            {/* Settings */}
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                  Kiosk location
                </label>
                <Input
                  value={form.location_label}
                  onChange={(e) => setForm((f) => ({ ...f, location_label: e.target.value }))}
                  placeholder="e.g. Main Warehouse — Front Entrance"
                  className="mt-1"
                  data-testid="kiosk-location-input"
                />
                <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[11px]" style={{ color: '#64748B' }}>
                  <span data-testid="kiosk-coords-label">
                    {form.latitude != null && form.longitude != null
                      ? `GPS: ${Number(form.latitude).toFixed(4)}, ${Number(form.longitude).toFixed(4)} — powers the weather greeting`
                      : 'No GPS coordinates set — capture them to show local weather on the kiosk screen.'}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (!navigator.geolocation) { toast.error('Geolocation is not available in this browser.'); return; }
                      navigator.geolocation.getCurrentPosition(
                        (pos) => {
                          setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
                          toast.success('Location captured — save to apply.');
                        },
                        () => toast.error('Could not get this device\u2019s location.'),
                        { enableHighAccuracy: true, timeout: 10000 },
                      );
                    }}
                    className="underline hover:text-white"
                    data-testid="kiosk-capture-location-btn"
                  >
                    Use this device&apos;s location
                  </button>
                  {form.latitude != null && (
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, latitude: null, longitude: null }))}
                      className="underline hover:text-white"
                      data-testid="kiosk-clear-location-btn"
                    >
                      clear
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                  Announcement (message of the day)
                </label>
                <textarea
                  value={form.announcement}
                  onChange={(e) => setForm((f) => ({ ...f, announcement: e.target.value.slice(0, 300) }))}
                  placeholder="e.g. Safety meeting at 9 AM in the break room — hard hats required today."
                  rows={2}
                  maxLength={300}
                  className="mt-1 w-full rounded-md px-3 py-2 text-sm text-white resize-none"
                  style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                  data-testid="kiosk-announcement-input"
                />
                <div className="flex items-center justify-between gap-3 mt-1.5 flex-wrap">
                  <p className="text-[11px]" style={{ color: '#64748B' }}>
                    Shown as a banner on the kiosk screen until you clear it. {form.announcement.length}/300
                  </p>
                  <label className="flex items-center gap-2 text-[11px]" style={{ color: '#64748B' }}>
                    Auto-expire on
                    <input
                      type="date"
                      value={form.announcement_expires_at}
                      onChange={(e) => setForm((f) => ({ ...f, announcement_expires_at: e.target.value }))}
                      className="rounded-md px-2 py-1 text-xs text-white"
                      style={{ background: '#0F1923', border: '1px solid #2D3748', colorScheme: 'dark' }}
                      data-testid="kiosk-announcement-expiry-input"
                    />
                    {form.announcement_expires_at && (
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, announcement_expires_at: '' }))}
                        className="underline hover:text-white"
                        data-testid="kiosk-announcement-expiry-clear"
                      >
                        clear
                      </button>
                    )}
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg px-3 py-2" style={{ border: '1px solid #2D3748' }}>
                <div>
                  <p className="text-sm text-white">Kiosk enabled</p>
                  <p className="text-[11px]" style={{ color: '#64748B' }}>Turn off to instantly deactivate the device link.</p>
                </div>
                <Switch
                  checked={form.enabled}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                  data-testid="kiosk-enabled-switch"
                />
              </div>

              <div className="rounded-lg px-3 py-2 space-y-3" style={{ border: '1px solid #2D3748' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Auto clock-out</p>
                    <p className="text-[11px]" style={{ color: '#64748B' }}>Automatically clock workers out if they forget.</p>
                  </div>
                  <Switch
                    checked={form.auto_clockout_enabled}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, auto_clockout_enabled: v }))}
                    data-testid="kiosk-autoclockout-switch"
                  />
                </div>
                {form.auto_clockout_enabled && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                        Max shift length (hours)
                      </label>
                      <Input
                        type="number" min="0.5" max="24" step="0.5"
                        value={form.max_shift_hours}
                        onChange={(e) => setForm((f) => ({ ...f, max_shift_hours: e.target.value }))}
                        placeholder="e.g. 9"
                        className="mt-1"
                        data-testid="kiosk-max-shift-input"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                        Fixed end-of-day time
                      </label>
                      <Input
                        type="time"
                        value={form.fixed_clockout_time}
                        onChange={(e) => setForm((f) => ({ ...f, fixed_clockout_time: e.target.value }))}
                        className="mt-1"
                        data-testid="kiosk-fixed-time-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-lg px-3 py-2" style={{ border: '1px solid #2D3748' }}>
                <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                  Shift start time (late-arrival flag)
                </label>
                <div className="flex items-center gap-3 mt-1">
                  <Input
                    type="time"
                    value={form.shift_start_time}
                    onChange={(e) => setForm((f) => ({ ...f, shift_start_time: e.target.value }))}
                    className="w-36"
                    data-testid="kiosk-shift-start-input"
                  />
                  <p className="text-[11px] flex-1" style={{ color: '#64748B' }}>
                    First punch-ins after this time get a "Late" flag on the daily summary email. Leave blank to disable.
                  </p>
                </div>
              </div>

              <div className="rounded-lg px-3 py-2 space-y-3" style={{ border: '1px solid #2D3748' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Photo on punch</p>
                    <p className="text-[11px]" style={{ color: '#64748B' }}>Snap a webcam photo at each punch so you can spot buddy-punching.</p>
                  </div>
                  <Switch
                    checked={form.photo_on_punch}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, photo_on_punch: v }))}
                    data-testid="kiosk-photo-switch"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Daily summary email</p>
                    <p className="text-[11px]" style={{ color: '#64748B' }}>Email supervisors around 7 PM with the day's punches and auto clock-outs.</p>
                  </div>
                  <Switch
                    checked={form.daily_summary_enabled}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, daily_summary_enabled: v }))}
                    data-testid="kiosk-summary-switch"
                  />
                </div>
              </div>

              {/* Clock-In Method */}
              <div className="mb-5">
                <p className="text-sm font-semibold text-white mb-1">Clock-In Method</p>
                <p className="text-xs mb-4" style={{ color: '#64748B' }}>
                  Control how workers on this team are required to clock in.
                </p>

                {[
                  {
                    value: 'any',
                    color: '#10B981',
                    bg: 'rgba(16,185,129,0.1)',
                    border: 'rgba(16,185,129,0.3)',
                    title: 'Any method',
                    desc: 'Workers can clock in via mobile app, QR code, or kiosk',
                  },
                  {
                    value: 'qr_required',
                    color: '#60A5FA',
                    bg: 'rgba(96,165,250,0.1)',
                    border: 'rgba(96,165,250,0.3)',
                    title: 'QR code required',
                    desc: 'Workers must scan the site QR poster — plain mobile is blocked',
                  },
                  {
                    value: 'kiosk_only',
                    color: '#A78BFA',
                    bg: 'rgba(168,85,247,0.1)',
                    border: 'rgba(168,85,247,0.3)',
                    title: 'Kiosk only',
                    desc: 'Workers must use the kiosk tablet — mobile and QR are both blocked',
                  },
                ].map((opt) => {
                  const selected = clockInMethod === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setClockInMethod(opt.value)}
                      className="w-full flex items-start gap-3 p-4 rounded-xl mb-2 text-left border-2 transition-all active:scale-[0.99]"
                      style={{
                        background: selected ? opt.bg : '#0F1923',
                        borderColor: selected ? opt.border : '#2D3748',
                      }}
                      data-testid={`clock-in-method-${opt.value}`}
                    >
                      <div
                        className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{
                          borderColor: selected ? opt.color : '#475569',
                          background: selected ? opt.color : 'transparent',
                        }}
                      >
                        {selected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white">{opt.title}</p>
                        <p className="text-xs mt-0.5 leading-relaxed" style={{ color: '#64748B' }}>{opt.desc}</p>
                      </div>
                    </button>
                  );
                })}

                {clockInMethod !== 'any' && (
                  <div
                    className="flex items-start gap-2 p-3 rounded-xl mt-1"
                    style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}
                    data-testid="clock-in-method-warning"
                  >
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#F59E0B' }} />
                    <p className="text-xs leading-relaxed" style={{ color: '#FCD34D' }}>
                      {clockInMethod === 'kiosk_only'
                        ? 'Workers will be blocked from mobile and QR clock-in. Make sure the kiosk is set up and workers know their PINs before enabling this.'
                        : 'Workers must scan the QR code every clock-in. Ensure QR posters are printed and posted at all entry points before enabling this.'}
                    </p>
                  </div>
                )}
              </div>

              <Button
                onClick={save}
                disabled={saving}
                className="w-full bg-[#1E88E5] hover:bg-[#1976D2] text-white"
                data-testid="kiosk-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {data?.exists ? 'Save kiosk settings' : 'Create kiosk'}
              </Button>
            </div>

            {/* Member PINs */}
            {kiosk && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: '#64748B' }}>
                  <KeyRound className="w-3.5 h-3.5" /> Worker PINs ({(data?.members || []).length} members)
                </p>
                <div className="rounded-lg divide-y max-h-64 overflow-y-auto" style={{ border: '1px solid #2D3748', borderColor: '#2D3748' }}>
                  {(data?.members || []).map((m) => (
                    <div key={m.id} className="flex items-center gap-2 px-3 py-2" style={{ borderColor: '#1F2D3F' }} data-testid={`kiosk-member-row-${m.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{m.name}</p>
                        {m.has_pin ? (
                          <p className="text-[10px] flex items-center gap-1" style={{ color: '#00C896' }}>
                            <CheckCircle2 className="w-3 h-3" /> PIN set
                          </p>
                        ) : (
                          <p className="text-[10px]" style={{ color: '#FFA000' }}>No PIN yet</p>
                        )}
                      </div>
                      <Input
                        value={pinInputs[m.id] || ''}
                        onChange={(e) => setPinInputs((p) => ({ ...p, [m.id]: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                        placeholder="4-digit PIN"
                        inputMode="numeric"
                        className="w-28 text-center font-mono"
                        data-testid={`kiosk-pin-input-${m.id}`}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPin(m.id)}
                        disabled={pinBusy === m.id || (pinInputs[m.id] || '').length !== 4}
                        data-testid={`kiosk-pin-save-${m.id}`}
                      >
                        {pinBusy === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (m.has_pin ? 'Reset' : 'Set')}
                      </Button>
                    </div>
                  ))}
                  {(data?.members || []).length === 0 && (
                    <p className="text-sm text-center py-6" style={{ color: '#64748B' }}>This team has no members yet.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
